import {
  ApiError,
  getErrorMessage,
  NetworkError,
  TimeoutError,
} from "./api-errors";
import { ApiEndpoints } from "./apiEndpoints";
import { fetchData, fetchWithEnvelope, setHeaderOptions } from "./apiHelpers";
import {
  FileStoreDetailResponse,
  FileStoreListResponse,
} from "./fileStore.type";

export type GetFileStoresParams = {
  page?: number;
  limit?: number;
  userId?: string;
  contactId?: string;
  expires?: number;
};

export const getFileStores = async (
  params: GetFileStoresParams = {}
): Promise<FileStoreListResponse> => {
  try {
    const searchParams = new URLSearchParams();
    const { page, limit, userId, contactId, expires } = params;

    if (page) searchParams.set("page", page.toString());
    if (limit) searchParams.set("limit", limit.toString());
    if (userId) searchParams.set("userId", userId);
    if (contactId) searchParams.set("contactId", contactId);
    if (expires) searchParams.set("expires", expires.toString());

    const queryString = searchParams.toString();
    const slug = `${ApiEndpoints.fileStore}${
      queryString ? `?${queryString}` : ""
    }`;

    const options = await setHeaderOptions({
      method: "GET",
    });

    return fetchWithEnvelope<FileStoreListResponse>(slug, options, 20000);
  } catch (error) {
    // Log with better context
    if (error instanceof ApiError) {
      console.error(`API Error (${error.status}): ${error.message}`);
    } else if (error instanceof NetworkError) {
      console.error(`Network Error: ${error.message}`);
    } else if (error instanceof TimeoutError) {
      console.error(`Timeout Error: ${error.message}`);
    } else {
      console.error("Error getting diagnosis:", getErrorMessage(error));
    }

    // Rethrow to be handled by the caller
    throw error;
  }
};

export const getFileStoreById = async (
  id: string
): Promise<FileStoreDetailResponse> => {
  try {
    const slug = `${ApiEndpoints.fileStore}/${encodeURIComponent(id)}`;
    const options = await setHeaderOptions({ method: "GET" });
    return fetchWithEnvelope<FileStoreDetailResponse>(slug, options, 20000);
  } catch (error) {
    console.error(`Error fetching file store by id (${id}):`, error);
    throw error;
  }
};

/**
 * Get a short-lived, signed URL for a file by id.
 * The endpoint returns `{ signed_url: string }` and we return the string.
 */
export const getSignedUrl = async (fileStoreId: string): Promise<string> => {
  try {
    const slug = `${ApiEndpoints.fileStore}/signed-url?id=${encodeURIComponent(
      fileStoreId
    )}`;

    const options = await setHeaderOptions({ method: "GET" });

    const { signed_url } = await fetchData<{ signed_url: string }>(
      slug,
      options
    );
    return signed_url;
  } catch (error) {
    // Reuse existing error logging style
    console.error("Failed to get signed URL:", error);
    throw error;
  }
};
